import { Component, EventEmitter, Input, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { IconName, ButtonVariant, ButtonType, ButtonSize, InputComponent } from '@yupma/ui-kit';
import { FieldOption } from 'server-model';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';

interface DisplayFieldOption extends FieldOption {
  isDisabled: boolean;
  isLabelInvalid: boolean;
  labelErrorText: string;
  isValueInvalid: boolean;
  valueErrorText: string;
}

@Component({
  selector: 'app-edit-field-option-customization',
  templateUrl: './edit-field-option-customization.component.html',
  styleUrl: './edit-field-option-customization.component.scss'
})
export class EditFieldOptionCustomizationComponent {
  @ViewChildren('labelInput') labelInputs!: QueryList<InputComponent>;
  @Input() option: FieldOption[] = [];
  @Input() isEdit: boolean = false;
  @Input() error: boolean = false;
  errorText: string = 'This field is required.';
  @Output() valueChange = new EventEmitter<FieldOption[]>();
  @Output() isFieldInvalid = new EventEmitter<boolean>();
  iconNames = IconName;
  buttonVariants = ButtonVariant;
  buttonTypes = ButtonType;
  buttonSizes = ButtonSize;
  displayOption: DisplayFieldOption[] = [];

  // ngOnChanges(changes: SimpleChanges) {
  //   if (!!(changes['option'])) {
  //     this.createDisplayFieldOption()
  //   }
  // }


  ngOnInit() {
    this.createDisplayFieldOption()
  }

  private createDisplayFieldOption() {
    if (this.option && this.option.length) {
      this.displayOption = this.option.map((option) => ({
        ...option,
        isDisabled: this.isEdit,
        isLabelInvalid: false,
        labelErrorText: '',
        isValueInvalid: false,
        valueErrorText: '',
      }));
    } else {
      this.displayOption = [{
        position: 0, label: "", value: '', isDisabled: false, isLabelInvalid: false, labelErrorText: '', isValueInvalid: false, valueErrorText: '',
      }]
    }
  }

  updateLabel(index: number, value: string) {
    if (!this.isEdit) {
      const trimmedValue = value.trim();
      this.displayOption[index].label = trimmedValue;

      this.updateValue(index, value)
    }
    // Trigger validation after updating the label
    // this.validateOption(index);
  }

  updateValue(index: number, newValue: string) {
    const trimmedValue = newValue.trim();
    this.displayOption[index].value = trimmedValue;

    // Trigger validation after updating the value
    this.validateAllOptions();
  }


  removeOption(index: number) {
    this.displayOption.splice(index, 1);

    // Validate all options after removing an option
    this.validateAllOptions();
  }

  addOption() {
    this.displayOption.push({
      position: 0,
      label: '',
      value: '',
      isDisabled: false,
      isLabelInvalid: false,
      labelErrorText: '',
      isValueInvalid: false,
      valueErrorText: '',
    });

    setTimeout(() => this.focusLastLabelInput(), 0);
  }

  private focusLastLabelInput() {
    const lastLabelInput = this.labelInputs.last;
    if (lastLabelInput) {
      lastLabelInput.focusInput(); // Focus the last label input
    }
  }

  drop(event: CdkDragDrop<DisplayFieldOption[]>) {
    moveItemInArray(this.displayOption, event.previousIndex, event.currentIndex);
  }


  validateAllOptions() {
    let hasErrors = false;

    this.displayOption.forEach((option, index) => {
      option.isLabelInvalid = false;
      option.labelErrorText = '';
      option.isValueInvalid = false;
      option.valueErrorText = '';

      // Check if at least one of label or value has been filled
      const isLabelOrValueFilled = option.label.trim() !== '' || option.value.trim() !== '';

      // Label validation
      if (isLabelOrValueFilled && !option.label.trim()) {
        option.isLabelInvalid = true;
        option.labelErrorText = 'Label is required.';
        hasErrors = true;
      } else if (isLabelOrValueFilled) {
        // Check for unique label only if label has been entered
        const duplicateLabel = this.displayOption.some(
          (opt, i) => i !== index && opt.label === option.label
        );
        if (duplicateLabel) {
          option.isLabelInvalid = true;
          option.labelErrorText = 'Label should be unique.';
          hasErrors = true;
        }
      }

      // Value validation
      if (isLabelOrValueFilled && !option.value.trim()) {
        option.isValueInvalid = true;
        option.valueErrorText = 'Internal name is required.';
        hasErrors = true;
      } else if (option.value.includes(';')) {
        option.isValueInvalid = true;
        option.valueErrorText = 'Internal name cannot contain a semicolon.';
        hasErrors = true;
      } else if (isLabelOrValueFilled) {
        // Check for unique value only if value has been entered
        const duplicateValue = this.displayOption.some(
          (opt, i) => i !== index && opt.value === option.value
        );
        if (duplicateValue) {
          option.isValueInvalid = true;
          option.valueErrorText = 'Internal name should be unique.';
          hasErrors = true;
        }
      }
    });

    // Emit whether any errors are present
    this.isFieldInvalid.emit(hasErrors);

    if (!hasErrors) {
      this.emitValueChange();
    }
  }

  emitValueChange() {
    const validOptions = this.displayOption
      .filter(option => option.label && option.value)
      .map((option, index) => ({
        position: index + 1,
        label: option.label,
        value: option.value,
        color: option.color
      }));

    this.valueChange.emit(validOptions);
  }

  sortOptionsAlphabetically() {
    // Sort displayOption based on label: first numerically, then alphabetically if needed
    this.displayOption.sort((a, b) => {
      const aLabelNum = parseFloat(a.label);
      const bLabelNum = parseFloat(b.label);

      const aIsNumeric = !isNaN(aLabelNum);
      const bIsNumeric = !isNaN(bLabelNum);

      if (aIsNumeric && bIsNumeric) {
        // Both labels are numeric, sort by numeric value
        return aLabelNum - bLabelNum;
      } else if (aIsNumeric) {
        // Only a is numeric, so a comes before b
        return -1;
      } else if (bIsNumeric) {
        // Only b is numeric, so b comes before a
        return 1;
      } else {
        // Both labels are non-numeric, sort alphabetically
        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
      }
    });
    this.validateAllOptions();
  }

}
